import type { Joi } from '@ksyos/koi';
import { get, set } from 'lodash';
import type { CustomControlCode, ServiceCode } from '@common/enums';
import type { MakeGivenKeysOptional } from '@common/utils/utilityTypes';
import type { ProcessActivityContextValue } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityContext';
import type { ProcessActivityEditContextValue } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityEdit/processActivityEditContext';
import type { ProcessActivityReadContextValue } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityRead/processActivityReadContext';
import { ProcessActivityContext } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityContext';
import { ProcessActivityEditContext } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityEdit/processActivityEditContext';
import { ProcessActivityReadContext } from '@frontend/components/pages/episodeCategory/carePath/process/processActivity/processActivityRead/processActivityReadContext';
import { useDefinedContext } from './useDefinedContext';

export function useProcessActivityContext<Code extends ServiceCode = ServiceCode>(): ProcessActivityContextValue<Code> {
    // `any` is necessary here due to the generic types of `ProcessActivityContextValue`
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return useDefinedContext<any>(ProcessActivityContext);
}

export function useServiceWorkflowStepParameters<Parameters = Record<string, unknown>>(
    schema: Joi.ObjectSchema<Parameters>
): Parameters {
    const { serviceWorkflowStep } = useProcessActivityContext();

    if (!serviceWorkflowStep?.parameters) {
        throw new Error('Expected to have parameters');
    }

    const validatedParameters = schema.validate(serviceWorkflowStep.parameters);
    if (validatedParameters.error) {
        throw validatedParameters.error;
    }

    return validatedParameters.value;
}

export function useProcessActivityReadContext<CustomControlData>(): ProcessActivityReadContextValue<CustomControlData> {
    return useDefinedContext(ProcessActivityReadContext);
}

export function useProcessActivityEditContext<
    Data extends Record<string, unknown> = Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
>(): ProcessActivityEditContextValue<Data, TCustomControlCode, Version> {
    return useDefinedContext(ProcessActivityEditContext) as ProcessActivityEditContextValue<
        Data,
        TCustomControlCode,
        Version
    >;
}

type Value = string | number | boolean | null;

type MergedProps = {
    error?: string;
    hasErrors: boolean;
    label: string;
    name: string;
    onChange: (value: Value) => void;
    value: Value;
};

export type ProcessActivityEditContextMergedProps<ComponentProps> =
    | (ComponentProps & { fieldKey?: string })
    | (MakeGivenKeysOptional<ComponentProps, keyof MergedProps & keyof ComponentProps> & { fieldKey?: string });

export function useProcessActivityEditContextMergedProps<ComponentProps>(
    props: ProcessActivityEditContextMergedProps<ComponentProps>
): ComponentProps | (ComponentProps & MergedProps) {
    const { fieldKey } = props;
    if (!fieldKey) {
        // This cast may be unnecessary, but requires some refactoring of the types
        return props as ComponentProps;
    }

    const { data, errors, onChange } = useProcessActivityEditContext<Record<string, Value>>();
    // This cast may be unnecessary, but requires some refactoring of the types
    const mergedProps = { ...props } as ComponentProps & MergedProps;

    if (typeof mergedProps.value === 'undefined') {
        mergedProps.value = get(data, fieldKey);
    }
    if (typeof mergedProps.onChange === 'undefined') {
        mergedProps.onChange = (value) => {
            // Only provide the partially changed data, `ProcessActivityEdit` will merge it with previous data.
            // Use Lodash `set` to support nested field keys (e.g. `patient.emailAddress`).
            const changedData = set({}, fieldKey, value);
            onChange(changedData, fieldKey);
        };
    }
    if (typeof mergedProps.error === 'undefined') {
        mergedProps.error = errors.get(fieldKey);
    }
    if (typeof mergedProps.hasErrors === 'undefined') {
        mergedProps.hasErrors = errors.has(fieldKey);
    }
    if (typeof mergedProps.name === 'undefined') {
        mergedProps.name = fieldKey;
    }
    if (typeof mergedProps.label === 'undefined') {
        mergedProps.label = fieldKey;
    }

    return mergedProps;
}
